import { Grid } from '@material-ui/core';
import { get } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-final-form';
import QCXFormSubtitle from '../../components/form-title/QCXFormSubtitle';
import QCXFormTitle from '../../components/form-title/QCXFormTitle';
import DominioDinamicoV2 from './components-fields-v2/DominioDinamicoV2';
import ListaEstaticaV2 from './components-fields-v2/ListaEstaticaV2';
import ValorInteiroV2 from './components-fields-v2/ValorInteiroV2';
import ValorRealV2 from './components-fields-v2/ValorRealV2';
import ValorTextoV2 from './components-fields-v2/ValorTextoV2';
import type { AtributoTratamentoNormalizado } from './QCXAtributosTratamentoTributarioDuimpV2';
import type { CombinacaoValida } from './components-fields-v2/types';

export interface CodigoNomeTributoDTOV3 {
  codigo: string;
  nome: string;
}

export interface CodigoNomeRegimeDTOV3 {
  codigo: string;
  nome: string;
}

export interface FundamentoLegalResponseDTOV3 {
  codigo: string;
  nome: string;
  tipo: string;
}

export interface AtributoTratamentoTributarioDTO {
  codigo: string;
  descricaoCodigo: string;
  formaPreenchimento: string;
  dominios: DominioAtributoTratamentoTributarioDTO[];
}

export interface DominioAtributoTratamentoTributarioDTO {
  valor: string;
  descricaoValor: string;
  atributos: AtributoTratamentoTributarioDTO[];
}

export interface TratamentoTributarioDTOV3 {
  tributo: CodigoNomeTributoDTOV3;
  regime: CodigoNomeRegimeDTOV3;
  fundamentoLegal: FundamentoLegalResponseDTOV3;
  atributos: AtributoTratamentoTributarioDTO[];
  codeTratamento?: string | null;
}

export interface TratamentoTributarioResponseDTOV3 {
  chave: number;
  tratamentos: TratamentoTributarioDTOV3;
}

type QCXAtributosTratamentoTributarioDuimpV3Props = {
  name: string;
  tributos: TratamentoTributarioResponseDTOV3[];
  isConsult?: boolean;
  showTitle?: boolean;
  showTreatmentTitle?: boolean;
};

type TratamentoNormalizado = {
  tratamento: TratamentoTributarioDTOV3;
  codTratamento: string;
  atributos: AtributoTratamentoNormalizado[];
  combinacoesValidas: CombinacaoValida[];
};

const normalizarAtributos = (
  atributos: AtributoTratamentoTributarioDTO[] = [],
  codTratamento: string
): AtributoTratamentoNormalizado[] => {
  const atributosPorCodigo = new Map<string, AtributoTratamentoNormalizado>();

  atributos.forEach((atributo) => {
    const atributoNormalizado = atributosPorCodigo.get(atributo.codigo);
    const dominios = (atributo.dominios || []).map((dominio) => ({
      valor: dominio.valor,
      descricao: dominio.descricaoValor,
    }));

    if (atributoNormalizado) {
      dominios.forEach((dominio) => {
        if (!atributoNormalizado.dominio.some((opcao) => opcao.valor === dominio.valor)) {
          atributoNormalizado.dominio.push(dominio);
        }
      });
      return;
    }

    atributosPorCodigo.set(atributo.codigo, {
      codigo: atributo.codigo,
      descricaoCodigo: atributo.descricaoCodigo,
      tipoCodigo: atributo.formaPreenchimento,
      valor: '',
      descricaoValor: '',
      codTratamento,
      dominio: dominios,
    });
  });

  return Array.from(atributosPorCodigo.values());
};

export default function QCXAtributosTratamentoTributarioDuimpV3({
  name,
  tributos,
  isConsult = false,
  showTitle = true,
  showTreatmentTitle = true,
}: QCXAtributosTratamentoTributarioDuimpV3Props) {
  const form = useForm();
  const { values } = useFormState();

  const tratamentosNormalizados = useMemo<TratamentoNormalizado[]>(
    () =>
      (tributos || [])
        .filter((item) => item?.tratamentos)
        .map(({ tratamentos: tratamento }) => {
          const codTratamento =
            tratamento.codeTratamento ||
            `${tratamento.tributo.codigo};${tratamento.regime.codigo};${tratamento.fundamentoLegal.codigo}`;

          return {
            tratamento,
            codTratamento,
            atributos: normalizarAtributos(tratamento.atributos, codTratamento),
            combinacoesValidas: [],
          };
        })
        .filter(({ atributos }) => atributos.length > 0),
    [tributos]
  );

  const ordemAtributosPorTratamento = useMemo(
    () =>
      Object.fromEntries(
        tratamentosNormalizados.map(({ tratamento, atributos }) => {
          const codigoTratamento = `${tratamento.tributo.codigo};${tratamento.regime.codigo};${tratamento.fundamentoLegal.codigo}`;

          return [codigoTratamento, atributos.map((atributo) => atributo.codigo)];
        })
      ),
    [tratamentosNormalizados]
  );

  const ordemAtual = values?.ignorableFields?.tratamentosTtce?.metadados?.ordemAtributosPorTratamento;

  const valoresFormulario = (get(values, name, {}) || {}) as Record<
    string,
    {
      codigo?: string;
      valor?: unknown;
      codTratamentosPossiveis?: string[];
    }
  >;

  useEffect(() => {
    const ordemAtualizada = { ...(ordemAtual || {}) };

    Object.entries(ordemAtributosPorTratamento).forEach(([codigoTratamento, atributos]) => {
      ordemAtualizada[codigoTratamento] = Array.from(
        new Set([...(ordemAtualizada[codigoTratamento] || []), ...atributos])
      );
    });

    if (JSON.stringify(ordemAtual) !== JSON.stringify(ordemAtualizada)) {
      form.change(
        'ignorableFields.tratamentosTtce.metadados.ordemAtributosPorTratamento',
        ordemAtualizada
      );
    }
  }, [form, ordemAtributosPorTratamento, ordemAtual]);

  useEffect(() => {
    const valoresAtualizados = { ...valoresFormulario };

    tratamentosNormalizados.forEach(({ tratamento, atributos }) => {
      const codigoTratamento = `${tratamento.tributo.codigo};${tratamento.regime.codigo};${tratamento.fundamentoLegal.codigo}`;

      atributos.forEach((atributo) => {
        const valorAtual = valoresAtualizados[atributo.codigo];
        const codTratamentosPossiveis = new Set(valorAtual?.codTratamentosPossiveis || []);

        codTratamentosPossiveis.add(codigoTratamento);
        valoresAtualizados[atributo.codigo] = {
          ...valorAtual,
          codigo: atributo.codigo,
          codTratamentosPossiveis: Array.from(codTratamentosPossiveis),
        };
      });
    });

    if (JSON.stringify(valoresAtualizados) !== JSON.stringify(valoresFormulario)) {
      form.change(name, valoresAtualizados);
    }
  }, [form, name, tratamentosNormalizados, valoresFormulario]);

  const renderAtributo = (atributo: AtributoTratamentoNormalizado, combinacoesValidas: CombinacaoValida[]) => {
    const tipoCodigo = String(atributo.tipoCodigo || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    const props = {
      atributo,
      basePath: name,
      valuesPath: name,
      combinacoesValidas,
      disabled: isConsult,
    };

    if (tipoCodigo === 'dominio dinamico') return <DominioDinamicoV2 {...props} />;
    if (tipoCodigo === 'lista estatica') return <ListaEstaticaV2 {...props} />;
    if (tipoCodigo === 'valor inteiro' || tipoCodigo === 'numero inteiro') {
      return <ValorInteiroV2 {...props} />;
    }
    if (tipoCodigo === 'valor real' || tipoCodigo === 'numero real') return <ValorRealV2 {...props} />;

    return <ValorTextoV2 {...props} />;
  };

  return (
    <>
      {showTitle ? (
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <QCXFormTitle title={'Atributos de Tratamento Tributario'.toUpperCase()} />
        </Grid>
      ) : null}

      {tratamentosNormalizados.map(({ tratamento, codTratamento, atributos, combinacoesValidas }) => (
        <Grid item xs={12} key={codTratamento}>
          {showTreatmentTitle ? (
            <QCXFormSubtitle
              title={`${tratamento.tributo.codigo} - ${tratamento.tributo.nome} | ${tratamento.regime.codigo} - ${tratamento.regime.nome} | ${tratamento.fundamentoLegal.codigo} - ${tratamento.fundamentoLegal.nome}`}
            />
          ) : null}
          <Grid container spacing={2}>
            {atributos.map((atributo) => (
              <Grid style={{ marginTop: 16 }} item xs={12} sm={6} md={4} key={`${codTratamento}-${atributo.codigo}`}>
                {renderAtributo(atributo, combinacoesValidas)}
              </Grid>
            ))}
          </Grid>
        </Grid>
      ))}
    </>
  );
}
